import { formatVehiclePlateToStr } from "@/utils/formatVehiclePlate";
import { mapDocToPost } from "@/utils/mapDocToPost.web";
import { getApp } from "firebase/app";
import {
  doc,
  collection as firestoreCollection,
  GeoPoint,
  getDoc,
  getFirestore,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { UploadPostArgs } from "./types";
import { uploadBlobWithRetry } from "./uploadRetry";

const firestore = getFirestore(getApp());
const storage = getStorage(getApp());

export const uploadPost = async ({
  images,
  user,
  customCoords,
  userData,
  groupId,
  eventDate,
  description,
  vehiclePlates,
  eventType,
  plateFollowersOnly,
  sourceUrl,
  anonymous,
  addPost,
}: UploadPostArgs) => {
  let imageUrls: string[] = [];
  for (let i = 0; i < images.length; i++) {
    const asset = images[i];
    let blob: Blob;
    let fileExtension = "jpg";

    if (asset.mimeType) {
      fileExtension = asset.mimeType.split("/")[1];
    } else if (!asset.uri.startsWith("data:")) {
      const ext = asset.uri.split(".").pop();
      if (ext && ext.length < 5) fileExtension = ext;
    }

    const filename = `posts/${user.uid}/${Date.now()}_${i}.${fileExtension}`;

    const pickerFile = (asset as unknown as { file?: File }).file;
    if (pickerFile && asset.type !== "video") {
      const buf = await pickerFile.arrayBuffer();
      blob = new Blob([buf], {
        type: pickerFile.type || `image/${fileExtension}`,
      });
    } else if (asset.uri.startsWith("data:")) {
      const arr = asset.uri.split(",");
      const mimeMatch = arr[0].match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      blob = new Blob([u8arr], { type: mime });
    } else {
      const response = await fetch(asset.uri);
      blob = await response.blob();
    }

    const storageRef = ref(storage, filename);
    await uploadBlobWithRetry(storageRef, blob, uploadBytes);
    const url = await getDownloadURL(storageRef);
    imageUrls.push(url);
  }

  let locationObj = null;
  if (customCoords) {
    let city = "";
    let country = "";
    try {
      if (
        typeof window !== "undefined" &&
        window.google &&
        window.google.maps
      ) {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({
          location: { lat: customCoords.latitude, lng: customCoords.longitude },
        });

        if (response.results && response.results.length > 0) {
          const result = response.results[0];
          for (const component of result.address_components) {
            if (component.types.includes("locality")) {
              city = component.long_name;
            }
            if (
              !city &&
              component.types.includes("administrative_area_level_1")
            ) {
              city = component.long_name;
            }
            if (component.types.includes("country")) {
              country = component.short_name;
            }
          }
        }
      }
    } catch (e) {
      console.warn("Reverse geocoding failed", e);
    }
    locationObj = {
      geoPoint: new GeoPoint(customCoords.latitude, customCoords.longitude),
      city,
      country,
    };
  }

  const newPostRef = doc(firestoreCollection(firestore, "posts"));
  const newPostData: any = {
    author: {
      uid: user.uid,
      ...(userData?.isVerified ? { isVerified: userData?.isVerified } : {}),
      ...(anonymous
        ? {}
        : {
            ...(userData?.displayName
              ? { displayName: userData?.displayName }
              : {}),
            ...(userData?.username ? { username: userData?.username } : {}),
            ...(userData?.photoURL ? { photoURL: userData?.photoURL } : {}),
          }),
    },
    createdAt: serverTimestamp(),
    groupId: groupId ?? null,
    eventDate: eventDate,
    description,
    vehiclePlates: vehiclePlates.map(formatVehiclePlateToStr),
    eventType,
    location: locationObj,
    images: imageUrls,
    plateFollowersOnly: vehiclePlates.length > 0 ? plateFollowersOnly : false,
    ...(sourceUrl ? { sourceUrl } : {}),
    ...(anonymous ? { anonymous: true } : {}),
  };

  await setDoc(newPostRef, newPostData);

  // Bump the group's lastPostAt (drives the unread dot on the groups list)
  // and stamp the poster's own groupsLastViewedAt so they don't see their
  // own post as unread. Fire-and-forget: a rules or network failure here
  // must not fail the post creation the user just completed.
  if (newPostData.groupId) {
    const gid = newPostData.groupId as string;
    try {
      await updateDoc(doc(firestore, "groups", gid), {
        lastPostAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("Failed to update group lastPostAt", err);
    }
    try {
      await updateDoc(doc(firestore, "users", user.uid), {
        [`groupsLastViewedAt.${gid}`]: serverTimestamp(),
      });
    } catch (err) {
      console.warn("Failed to update groupsLastViewedAt for poster", err);
    }
  }

  const newPostSnap = await getDoc(newPostRef);
  const newPost = mapDocToPost(newPostSnap);

  if (!newPostData.plateFollowersOnly && newPostData.groupId === null) {
    addPost(newPost);
  }
};
